import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import { effectiveAppRole } from '../utils/roles.js';
import { clearHumanCycleCache } from '../utils/humanCycles.js';
import {
    resolveLoginPath,
    saveUserLastPath,
} from '../utils/sessionNavigation.js';

const AuthContext = createContext();

/** Unifica `rol` / `role` y normaliza `trabajador` → `worker` para layout y rutas. */
function normalizeSessionUser(raw) {
    if (!raw) return raw;
    const canonical = effectiveAppRole(raw.rol ?? raw.role) ?? raw.rol ?? raw.role;
    return {
        ...raw,
        rol: canonical,
        role: canonical,
        tenantId: raw.tenantId ?? raw.tenant_id,
        tenantName: raw.tenantName ?? raw.tenant_name,
        tenantSlug: raw.tenantSlug ?? raw.tenant_slug,
        nombre_completo: raw.nombre_completo ?? raw.full_name ?? raw.name,
        hasProfile: Boolean(raw.hasProfile ?? raw.has_profile ?? false),
        needsRegistroManada:
            raw.needsRegistroManada ??
            raw.needs_registro_manada ??
            !(raw.hasProfile ?? raw.has_profile),
        solutionProfile: raw.solutionProfile ?? raw.solution_profile ?? 'rotacion',
        capabilities: raw.capabilities || null,
    };
}

function readStoredPlatform() {
    try {
        const raw = localStorage.getItem('alinea_platform');
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [platform, setPlatform] = useState(readStoredPlatform);
    const [loading, setLoading] = useState(true);

    function persistPlatform(next) {
        if (next) {
            setPlatform(next);
            localStorage.setItem('alinea_platform', JSON.stringify(next));
        } else {
            setPlatform(null);
            localStorage.removeItem('alinea_platform');
        }
    }

    // Inicializar cargando el usuario desde localStorage si existe el token
    useEffect(() => {
        const initAuth = async () => {
            const token = localStorage.getItem('alinea_token');
            const savedUser = localStorage.getItem('alinea_user');

            if (token && savedUser) {
                try {
                    setUser(normalizeSessionUser(JSON.parse(savedUser)));
                    const res = await authAPI.getMe();
                    const me = normalizeSessionUser(res.data.data);
                    setUser(me);
                    localStorage.setItem('alinea_user', JSON.stringify(me));
                    if (me?.platform) persistPlatform(me.platform);
                } catch (err) {
                    console.error('Error validando sesión:', err);
                    localStorage.removeItem('alinea_token');
                    localStorage.removeItem('alinea_user');
                    persistPlatform(null);
                    setUser(null);
                }
            }
            setLoading(false);
        };

        initAuth();
    }, []);

    const login = async (codigo, contrasena = null, tenantSlug = null) => {
        try {
            setLoading(true);
            const res = await authAPI.login(codigo, contrasena, tenantSlug);
            const body = res.data ?? {};
            const status = res.status;

            if (status === 401 || status === 403 || status === 400) {
                return {
                    success: false,
                    message: body.error || body.message || 'Error al iniciar sesión',
                    passwordRequired: !!body.passwordRequired,
                    code: body.code || null,
                };
            }

            if (body?.success === false) {
                if (body.passwordRequired) {
                    return {
                        success: false,
                        passwordRequired: true,
                        message: body.error || 'Introduce tu contraseña para continuar',
                    };
                }
                return {
                    success: false,
                    message: body.error || 'Error al iniciar sesión',
                    code: body.code || null,
                };
            }

            const { token, user, branding, platform: platformFlags } = body.data;

            localStorage.setItem('alinea_token', token);

            let sessionUser = normalizeSessionUser(user);
            const fresh = await authAPI.getMe().catch(() => null);
            if (fresh?.data?.data) {
                sessionUser = normalizeSessionUser(fresh.data.data);
            }
            setUser(sessionUser);
            localStorage.setItem('alinea_user', JSON.stringify(sessionUser));
            if (sessionUser?.rol !== 'superadmin') {
                localStorage.removeItem('alinea_superadmin_tenant_id');
            } else if (sessionUser?.tenantId) {
                localStorage.setItem('alinea_superadmin_tenant_id', String(sessionUser.tenantId));
            }
            persistPlatform(sessionUser?.platform ?? platformFlags ?? null);
            return { success: true, user: sessionUser, branding, platform: platformFlags };
        } catch (err) {
            const status = err.response?.status;
            const data = err.response?.data || {};
            const base = data.error || data.message || 'Error al iniciar sesión';
            const expectedAuth =
                status === 401 ||
                status === 403 ||
                data.passwordRequired ||
                data.code === 'TENANT_LOGIN_REQUIRED';
            if (!expectedAuth) {
                console.error('Login failed:', err);
            }
            const message =
                status === 429
                    ? `${base} Espere un minuto e intente de nuevo.`
                    : base;
            return {
                success: false,
                message,
                passwordRequired: !!data.passwordRequired,
                code: data.code || null,
            };
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        let sessionUser = null;
        try {
            sessionUser = JSON.parse(localStorage.getItem('alinea_user') || 'null');
        } catch {
            sessionUser = user;
        }
        if (sessionUser && typeof window !== 'undefined') {
            saveUserLastPath(
                sessionUser,
                `${window.location.pathname}${window.location.search}`
            );
        }
        const loginPath = resolveLoginPath({ user: sessionUser });
        localStorage.removeItem('alinea_token');
        localStorage.removeItem('alinea_user');
        localStorage.removeItem('alinea_superadmin_tenant_id');
        clearHumanCycleCache();
        persistPlatform(null);
        setUser(null);
        window.location.href = loginPath;
    };

    /** Actualiza usuario desde el servidor (p. ej. tras completar Registro de manada). */
    const refreshUser = async () => {
        const token = localStorage.getItem('alinea_token');
        if (!token) return null;
        try {
            const res = await authAPI.getMe();
            const next = normalizeSessionUser(res.data?.data);
            if (next) {
                setUser(next);
                localStorage.setItem('alinea_user', JSON.stringify(next));
                if (next.platform) persistPlatform(next.platform);
            }
            return next;
        } catch (err) {
            console.error('Error refrescando usuario:', err);
            return null;
        }
    };

    return (
        <AuthContext.Provider value={{ user, platform, loading, login, logout, refreshUser }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth debe ser usado dentro de un AuthProvider');
    }
    return context;
};
